import Button from '@jetbrains/ring-ui/components/button/button'
import Toggle from '@jetbrains/ring-ui/components/toggle/toggle'
import classNames from 'classnames'
import plur from 'plur'
import {memo, useCallback, useContext, useEffect, useState} from 'react'
import type {ComponentType} from 'react'

import {restApi} from '../../../../../../services/rest'
import {emptyArray, noop} from '../../../../../../utils/empty'
import {GetRulesArgContext} from '../../../CleanupProjectPage.contexts'
import {useIsReadOnly} from '../../../CleanupProjectPage.hooks'
import type {Rule, RuleId} from '../Rules.types'
import {getKey, getRulesByOwnership} from '../Rules.utils'

import RuleRow from './RuleItem/RuleItem'
import type {Props} from './RulesList.types'

import styles from './RulesList.css'

const RulesList: ComponentType<Props> = memo<Props>(function RulesList({
  embedded,
  disableButtons,
  onRuleOpenClick = noop,
  holderNode,
  projectId,
  inheritedVisible = true,
  hideOwn = false,
  hideInherited = false,
  hideEnforced = false,
  readOnlyProjectsKey,
}) {
  const arg = useContext(GetRulesArgContext)
  const {rules} = restApi.endpoints.getCleanupRules.useQuery(arg, {
    selectFromResult: ({data}) => ({rules: data?.[getKey(holderNode)]?.rules ?? emptyArray}),
  })
  const [deleteRule] = restApi.endpoints.deleteCleanupRule.useMutation()
  const [createRule] = restApi.endpoints.createCleanupRule.useMutation()
  const [updateRule] = restApi.endpoints.updateCleanupRule.useMutation()

  const onRuleDeleteClick = useCallback(
    (ruleId: RuleId) => deleteRule({holderNode, ruleId}),
    [deleteRule, holderNode],
  )

  const toggleDisabled = useCallback(
    (rule: Rule) => {
      const ruleDisabled = !rule.ruleDisabled
      const nextRule: Rule = {...rule, source: 'own', ruleDisabled}
      // If rule is inherited, then we create a new own empty one with the same ID
      rule.source === 'own'
        ? updateRule({holderNode, rule: nextRule})
        : createRule({holderNode, rule: nextRule, ruleId: nextRule.ruleId})
    },
    [updateRule, createRule, holderNode],
  )

  const readOnly = useIsReadOnly(projectId, readOnlyProjectsKey)
  const [showInherited, toggleInherited] = useState(inheritedVisible)
  useEffect(() => toggleInherited(inheritedVisible), [inheritedVisible])
  const onToggleInherited = useCallback(() => toggleInherited(state => !state), [])
  const {enforced, own, inherited} = getRulesByOwnership(rules)

  const getRuleRow = (rulesList: Rule[]) =>
    rulesList.map(rule => (
      <RuleRow
        holderNode={holderNode}
        disableButtons={disableButtons}
        key={rule.ruleId}
        rule={rule}
        onRuleOpenClick={onRuleOpenClick}
        onRuleDeleteClick={onRuleDeleteClick}
        onRuleDisableClick={toggleDisabled}
        projectId={projectId}
        readOnlyProjectsKey={readOnlyProjectsKey}
      />
    ))

  const showInheritedToggle = embedded !== true && (enforced.length > 0 || inherited.length > 0)
  const showOwnRules = !hideOwn
  const showInheritedRules = !hideInherited && inherited.length > 0 && showInherited
  const showEnforcedRules = !hideEnforced && enforced.length > 0 && showInherited
  const showTopActionsSection =
    !readOnly && ((!hideInherited && !hideEnforced && !hideOwn) || (hideInherited && hideEnforced))
  return (
    <>
      {showInheritedToggle && (
        <div className={styles.inheritedToggle}>
          <Toggle checked={showInherited} onChange={onToggleInherited}>
            {`Show ${enforced.length + inherited.length} inherited ${plur(
              'rule',
              enforced.length + inherited.length,
            )}`}
          </Toggle>
        </div>
      )}
      {showTopActionsSection && (
        <div className={classNames(styles.addNewRule)}>
          <Button
            className={styles.ruleButton}
            onClick={() => onRuleOpenClick()}
            disabled={disableButtons}
          >
            {'Add rule'}
          </Button>
        </div>
      )}
      {showOwnRules && getRuleRow(own)}
      {showInheritedRules && getRuleRow(inherited)}
      {showEnforcedRules && getRuleRow(enforced)}
    </>
  )
})
export default RulesList
